import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "wouter";

type Msg = { role: "user" | "assistant"; content: string };
type ToolResult = { tool: string; ok: boolean; detail: string; ref?: string };

const KNOWN_PATHS: Record<string, string> = {
  "/quote": "Get a free quote",
  "/contact": "Contact us",
  "/register": "Register an account",
  "/login": "Customer login",
  "/portal": "Customer portal",
  "/hvac": "Explore our HVAC services",
  "/hvac/ventilation": "Ventilation services",
  "/hvac/heating": "Heating services",
  "/hvac/ductwork": "Ductwork services",
  "/hvac/controls": "HVAC controls",
  "/installation": "AC installation",
  "/repair": "AC repair",
  "/maintenance": "Maintenance & PPM",
};

function extractPaths(text: string): string[] {
  const re = /(?:^|[\s(\[])(\/[a-z][a-z0-9-]*(?:\/[a-z0-9-]+)*)/gi;
  const found = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const path = m[1].replace(/[.,!?;:)\]]+$/, "");
    if (KNOWN_PATHS[path]) found.add(path);
  }
  return Array.from(found);
}

const STORAGE_KEY = "vertexair_chat";
const GREETING: Msg = {
  role: "assistant",
  content: "Hi! I'm Vertex 👋 I can help with quotes, bookings, and questions about our AC & HVAC services. How can I help today?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch {}
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next }),
      });
      const data: { reply?: string; toolResults?: ToolResult[]; error?: string } = await res.json();
      let reply = data.reply || data.error || "Sorry, something went wrong.";
      const tr = data.toolResults || [];
      if (tr.length) {
        const notes = tr
          .filter(t => t.ok)
          .map(t => t.tool === "create_lead"
            ? `✅ Lead saved (${t.ref})`
            : t.tool === "book_appointment"
              ? `📅 Booking provisionally held (${t.ref})`
              : `✅ ${t.detail}`)
          .join("\n");
        if (notes) reply = `${reply}\n\n${notes}`;
      }
      setMessages(m => [...m, { role: "assistant", content: reply }]);
      if (!open) setUnread(u => u + 1);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again or email info@vertexclimate.co.uk." }]);
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function clearChat() {
    setMessages([GREETING]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-full shadow-2xl px-4 py-3 transition-all hover:scale-105 group"
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5" />
            {unread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>
            )}
          </div>
          <span className="text-sm font-semibold hidden sm:inline">Chat with us</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[min(600px,calc(100vh-3rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-blue-700 text-white px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Vertex</div>
              <div className="text-xs text-white/80 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Usually replies in seconds
              </div>
            </div>
            <button onClick={clearChat} title="Start over" className="text-white/80 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10">
              Reset
            </button>
            <button onClick={() => setOpen(false)} aria-label="Close chat" className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => {
              const paths = m.role === "assistant" ? extractPaths(m.content) : [];
              return (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}>
                    <div
                      className={`rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === "user"
                          ? "bg-primary text-white rounded-br-sm"
                          : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                    {paths.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {paths.map(p => (
                          <Link key={p} href={p}>
                            <a
                              onClick={() => setOpen(false)}
                              className="inline-flex items-center gap-1.5 bg-primary/10 hover:bg-primary hover:text-white text-primary text-xs font-semibold px-3 py-1.5 rounded-full border border-primary/20 transition-colors"
                            >
                              {KNOWN_PATHS[p]}
                              <ArrowRight className="w-3 h-3" />
                            </a>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Typing…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Type your message…"
                className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary max-h-32"
                disabled={loading}
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                aria-label="Send"
                className="bg-primary hover:bg-primary/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-slate-400 mt-2 text-center">
              Powered by AI · Don't share sensitive personal data
            </div>
          </div>
        </div>
      )}
    </>
  );
}
